import type { QueryClient } from '@tanstack/react-query';
import type { Asset, AssetCard, AssetVersion, Board, Comment, CommentPage, WsEventEnvelope } from '@/lib/api/types';
import { keys } from '@/lib/api/keys';
import type { Infinite } from '@/features/upload/reconcile';
import { boardHasAsset } from '@/features/upload/reconcile';

/**
 * §4.6 — pure query-cache patches for each event type. Every function takes the QueryClient and an
 * envelope and never throws on unexpected payloads. Side effects outside the cache (uploads, presence,
 * toasts) are routed through `hooks`, injected by RealtimeProvider.
 */
export type ApplyHooks = {
  onVersionState?: (assetId: string, versionId: string, state: string, progress: number | null | undefined, card: AssetCard | null, error?: { message?: string; retryable?: boolean }) => void;
  onVersionActivated?: (assetId: string, card: AssetCard | null, info?: { versionId: string; previousActiveVersionId: string | null; clientUploadId: string | null }) => void;
  clientUploadFor?: (assetId: string) => string | undefined;
  onPresence?: (room: string, viewers: unknown[]) => void;
  onDownloadReady?: (p: { job_id: string; url: string; expires_at: string; asset_count: number }) => void;
  /** which asset the detail overlay currently shows (for `asset.updated` invalidation) */
  openAssetId?: () => string | null;
};

type P = Record<string, unknown>;

export function upsertComment(page: CommentPage | undefined, comment: Comment, temp_id?: string | null): CommentPage | undefined {
  if (!page) return page;
  const items = page.items.slice();
  const replaceIn = (list: Comment[]): boolean => {
    const i = list.findIndex((c) => c.id === comment.id || (temp_id && c.temp_id === temp_id) || (comment.temp_id && c.temp_id === comment.temp_id));
    if (i >= 0) {
      list[i] = { ...list[i]!, ...comment, replies: comment.replies ?? list[i]!.replies };
      return true;
    }
    return false;
  };
  if (comment.parent_comment_id) {
    const pi = items.findIndex((c) => c.id === comment.parent_comment_id);
    if (pi >= 0) {
      const parent = { ...items[pi]!, replies: (items[pi]!.replies ?? []).slice() };
      if (!replaceIn(parent.replies!)) {
        parent.replies!.push(comment);
        parent.reply_count = (parent.reply_count ?? 0) + 1;
      }
      items[pi] = parent;
      return { ...page, items };
    }
  }
  if (!replaceIn(items)) {
    items.push(comment);
    return { ...page, items, total: (page.total ?? items.length - 1) + 1, unresolved_count: (page.unresolved_count ?? 0) + (comment.resolved || comment.is_system ? 0 : 1) };
  }
  return { ...page, items };
}

export function removeComment(page: CommentPage | undefined, commentId: string, parentId?: string | null): CommentPage | undefined {
  if (!page) return page;
  if (parentId) {
    const items = page.items.map((c) => (c.id === parentId && c.replies?.some((r) => r.id === commentId) ? { ...c, replies: c.replies.filter((r) => r.id !== commentId), reply_count: Math.max(0, (c.reply_count ?? 1) - 1) } : c));
    return { ...page, items };
  }
  const gone = page.items.find((c) => c.id === commentId);
  if (!gone) return page;
  return { ...page, items: page.items.filter((c) => c.id !== commentId), total: Math.max(0, (page.total ?? 1) - 1), unresolved_count: Math.max(0, (page.unresolved_count ?? 0) - (gone.resolved ? 0 : 1)) };
}

/** Apply `fn` to every cached copy of a card: board pages, share-board pages, search results. */
export function mapCardInPages(qc: QueryClient, id: string, fn: (card: AssetCard) => AssetCard): boolean {
  let any = false;
  const mapFlat = (old: Infinite | undefined) => {
    if (!old) return old;
    let touched = false;
    const pages = old.pages.map((p) => {
      const i = p.items.findIndex((c) => c.id === id);
      if (i < 0) return p;
      touched = true;
      const items = p.items.slice();
      items[i] = fn(items[i] as AssetCard);
      return { ...p, items };
    });
    if (touched) any = true;
    return touched ? { ...old, pages } : old;
  };
  qc.setQueriesData<Infinite>({ queryKey: ['board-assets'] }, mapFlat);
  // share pages cache plain cards too (keys.shareBoardAssets)
  qc.setQueriesData<Infinite>({ queryKey: ['share-board-assets'] }, mapFlat);
  qc.setQueriesData<{ pages: { items: { asset: AssetCard }[] }[] }>({ queryKey: ['search'] }, (old) => {
    if (!old) return old;
    let touched = false;
    const pages = old.pages.map((p) => {
      const i = p.items.findIndex((r) => r.asset.id === id);
      if (i < 0) return p;
      touched = true;
      const items = p.items.slice();
      items[i] = { ...items[i]!, asset: fn(items[i]!.asset) };
      return { ...p, items };
    });
    return touched ? { ...old, pages } : old;
  });
  return any;
}

export function patchCardInPages(qc: QueryClient, card: Partial<AssetCard> & { id: string }): boolean {
  return mapCardInPages(qc, card.id, (c) => ({ ...c, ...card }));
}

/** Keep the card's comment badges in step with comment events (cards and the open asset query). */
export function bumpCommentCounts(qc: QueryClient, assetId: string, delta: { comments?: number; unresolved?: number }): void {
  const dc = delta.comments ?? 0;
  const du = delta.unresolved ?? 0;
  if (!dc && !du) return;
  const bump = <T extends { comment_count?: number; unresolved_comment_count?: number }>(c: T): T => ({
    ...c,
    comment_count: Math.max(0, (c.comment_count ?? 0) + dc),
    unresolved_comment_count: Math.max(0, (c.unresolved_comment_count ?? 0) + du),
  });
  mapCardInPages(qc, assetId, bump);
  qc.setQueryData<Asset>(keys.asset(assetId), (old) => (old ? bump(old) : old));
}

export function removeAssetFromBoard(qc: QueryClient, boardId: string, assetId: string): void {
  qc.setQueriesData<Infinite>({ queryKey: ['board-assets', boardId] }, (old) => {
    if (!old) return old;
    let removed = 0;
    const pages = old.pages.map((p) => {
      const items = p.items.filter((c) => c.id !== assetId);
      if (items.length === p.items.length) return p;
      removed += p.items.length - items.length;
      return { ...p, items };
    });
    if (!removed) return old;
    const first = pages[0]!;
    pages[0] = { ...first, total: first.total == null ? first.total : Math.max(0, first.total - removed) };
    return { ...old, pages };
  });
}

export function prependAssetToBoard(qc: QueryClient, boardId: string, card: AssetCard): void {
  for (const q of qc.getQueryCache().findAll({ queryKey: ['board-assets', boardId] })) {
    const params = q.queryKey[2] as { sort?: string } | undefined;
    if (params?.sort && params.sort !== 'added_at:desc') {
      void qc.invalidateQueries({ queryKey: q.queryKey });
      continue;
    }
    qc.setQueryData<Infinite>(q.queryKey, (old) => {
      if (!old || old.pages.length === 0) return old;
      if (old.pages.some((p) => p.items.some((c) => c.id === card.id))) return old;
      const first = old.pages[0]!;
      const pages = old.pages.slice();
      pages[0] = { ...first, items: [card, ...first.items], total: first.total == null ? first.total : first.total + 1 };
      return { ...old, pages };
    });
  }
}

const roomId = (room: string) => room.slice(room.indexOf(':') + 1);

/**
 * Contract events that intentionally produce no cache patch: `upload.*` are derived (the `version.*` /
 * `asset.*` events carry the state), `annotation.*` are reserved in v1 (annotations ride inside comments).
 * `apply.test.ts` asserts every other `WS_EVENT_TYPES` entry is handled.
 */
export const WS_IGNORED_EVENT_TYPES: readonly string[] = ['upload.completed', 'upload.aborted', 'annotation.created', 'annotation.updated', 'annotation.deleted', ...release2Pending(), ...release3Pending()];

/**
 * Release 2 events (docs/specs/release2-contract.md) not handled yet: the owning lane deletes its type here when it
 * registers the applier (A: none; B: field_policy.changed, tag_group.changed, workspace_policy.changed (shared with C);
 * C: rights.expired, retention.applied).
 */
function release2Pending(): string[] {
  return [];
}

/**
 * Release 3 events (docs/specs/release3-contract.md) not handled yet: the owning lane deletes its type here when it
 * registers the applier (A: done — notification.created, notification.read; C: preset.changed).
 */
function release3Pending(): string[] {
  return [];
}

/** Dispatch one envelope into the cache. Returns the handled type, or `null` when nothing handles it (tests/telemetry). */
export function applyEvent(qc: QueryClient, env: WsEventEnvelope, hooks: ApplyHooks = {}): string | null {
  const p = (env.payload ?? {}) as P;
  const assetId = (p.asset_id as string | undefined) ?? (env.room.startsWith('asset:') ? roomId(env.room) : undefined);
  const wsId = (p.workspace_id as string | undefined) ?? (env.room.startsWith('workspace:') ? roomId(env.room) : undefined);
  for (const tap of RELEASE2_TAPS) tap(qc, env, p, wsId); // release2/A (side effects only; the switch below still claims the type)
  switch (env.type) {
    case 'comment.created':
    case 'comment.updated':
    case 'comment.resolved':
    case 'comment.unresolved': {
      const comment = p.comment as Comment | undefined;
      if (!assetId || !comment) break;
      qc.setQueryData<CommentPage>(keys.comments(assetId), (old) => upsertComment(old, comment, p.temp_id as string | null | undefined));
      if (!comment.parent_comment_id && !comment.is_system) {
        if (env.type === 'comment.created') bumpCommentCounts(qc, assetId, { comments: 1, unresolved: comment.resolved ? 0 : 1 });
        else if (env.type === 'comment.resolved') bumpCommentCounts(qc, assetId, { unresolved: -1 });
        else if (env.type === 'comment.unresolved') bumpCommentCounts(qc, assetId, { unresolved: 1 });
      }
      break;
    }
    case 'comment.deleted': {
      if (!assetId || typeof p.comment_id !== 'string') break;
      const gone = p.parent_comment_id ? undefined : qc.getQueryData<CommentPage>(keys.comments(assetId))?.items.find((c) => c.id === p.comment_id);
      qc.setQueryData<CommentPage>(keys.comments(assetId), (old) => removeComment(old, p.comment_id as string, p.parent_comment_id as string | null));
      if (!p.parent_comment_id) bumpCommentCounts(qc, assetId, { comments: -1, unresolved: gone && !gone.resolved && !gone.is_system ? -1 : 0 });
      break;
    }
    case 'asset.updated': {
      const card = p.asset as AssetCard | undefined;
      if (!assetId) break;
      if (card) {
        patchCardInPages(qc, card);
        qc.setQueryData<Asset>(keys.asset(assetId), (old) => (old ? { ...old, ...card, media: old.media, tags: old.tags, custom_field_values: old.custom_field_values } : old));
      }
      const changed = (p.changed as string[] | undefined) ?? [];
      const open = hooks.openAssetId?.() === assetId;
      if (open || changed.some((c) => c !== 'title' && c !== 'approval_status') || (card && qc.getQueryData<Asset>(keys.asset(assetId))?.active_version_id !== card.active_version_id)) {
        void qc.invalidateQueries({ queryKey: keys.asset(assetId) });
      }
      break;
    }
    case 'asset.processing':
    case 'version.state_changed': {
      const versionId = p.version_id as string;
      const state = p.state as string;
      const card = (p.card as AssetCard | undefined) ?? null;
      if (!assetId || !versionId) break;
      const progress = typeof p.progress === 'number' ? p.progress : null;
      hooks.onVersionState?.(assetId, versionId, state, progress, card, { message: p.error_message as string | undefined, retryable: p.retryable as boolean | undefined });
      if (card && !hooks.clientUploadFor?.(assetId)) patchCardInPages(qc, card);
      else if (!card) patchCardInPages(qc, { id: assetId, version_state: state as AssetCard['version_state'], processing_progress: progress });
      if (p.version) {
        const v = p.version as AssetVersion;
        qc.setQueryData<AssetVersion[]>(keys.versions(assetId), (old) => (old ? old.map((x) => (x.id === v.id ? { ...x, ...v } : x)) : old));
        qc.setQueryData<Asset>(keys.asset(assetId), (old) => (old && old.active_version?.id === v.id ? { ...old, active_version: { ...old.active_version, ...v }, version_state: v.state } : old));
      }
      if (state === 'ready' || state === 'failed') {
        void qc.invalidateQueries({ queryKey: keys.processing(versionId) });
        if (state === 'ready') void qc.invalidateQueries({ queryKey: keys.asset(assetId) });
      }
      break;
    }
    case 'version.created': {
      if (!assetId) break;
      const v = p.version as AssetVersion | undefined;
      if (v) qc.setQueryData<AssetVersion[]>(keys.versions(assetId), (old) => (old && !old.some((x) => x.id === v.id) ? [v, ...old] : old));
      void qc.invalidateQueries({ queryKey: keys.versions(assetId) });
      break;
    }
    case 'version.activated': {
      if (!assetId) break;
      const v = p.version as AssetVersion | undefined;
      void qc.invalidateQueries({ queryKey: keys.asset(assetId) });
      void qc.invalidateQueries({ queryKey: keys.versions(assetId) });
      if (v) {
        const partial: Partial<AssetCard> & { id: string } = { id: assetId, active_version_id: v.id, version_state: v.state, ...(v.thumbnails ? { thumbnails: v.thumbnails } : {}), ...(v.width && v.height ? { width: v.width, height: v.height, aspect_ratio: v.width / v.height } : {}) };
        patchCardInPages(qc, partial);
        hooks.onVersionActivated?.(assetId, null, { versionId: v.id, previousActiveVersionId: (p.previous_active_version_id as string | null | undefined) ?? null, clientUploadId: (p.client_upload_id as string | null | undefined) ?? null });
      }
      break;
    }
    case 'version.archived':
    case 'version.unarchived':
    case 'version.deleted': {
      if (!assetId) break;
      const v = p.version as AssetVersion | undefined;
      if (v && env.type === 'version.deleted') qc.setQueryData<AssetVersion[]>(keys.versions(assetId), (old) => (old ? old.filter((x) => x.id !== v.id) : old));
      else if (v) qc.setQueryData<AssetVersion[]>(keys.versions(assetId), (old) => (old ? old.map((x) => (x.id === v.id ? { ...x, ...v } : x)) : old));
      void qc.invalidateQueries({ queryKey: keys.versions(assetId) });
      void qc.invalidateQueries({ queryKey: keys.asset(assetId) });
      break;
    }
    case 'asset.created':
    case 'asset.restored': {
      const boardId = (p.board_id as string | undefined) ?? (env.room.startsWith('board:') ? roomId(env.room) : undefined);
      const card = p.asset as AssetCard | undefined;
      const restored = env.type === 'asset.restored';
      if (restored && card) qc.setQueryData<Asset>(keys.asset(card.id), (old) => (old ? { ...old, ...card, media: old.media, tags: old.tags, custom_field_values: old.custom_field_values, deleted_at: null } : old));
      if (!boardId) break;
      // our own upload: the placeholder is already in place (§3.6); a restore never has a placeholder
      const cu = restored ? null : ((p.client_upload_id as string | null | undefined) ?? (card ? hooks.clientUploadFor?.(card.id) : undefined));
      if (cu) break;
      if (card) {
        if (!boardHasAsset(qc, boardId, card.id)) prependAssetToBoard(qc, boardId, card);
      } else void qc.invalidateQueries({ queryKey: ['board-assets', boardId] });
      void qc.invalidateQueries({ queryKey: keys.board(boardId) });
      if (restored) void qc.invalidateQueries({ queryKey: ['search'] });
      break;
    }
    case 'asset.moved': {
      // reorder inside a board (sort_order) or a move between boards (from_board_id): the manual sort pages
      // of the target are position-based, so refetch them; the source just loses the card
      const boardId = (p.board_id as string | undefined) ?? (env.room.startsWith('board:') ? roomId(env.room) : undefined);
      const id = p.asset_id as string | undefined;
      const from = p.from_board_id as string | null | undefined;
      if (!boardId || !id) break;
      if (from && from !== boardId) {
        removeAssetFromBoard(qc, from, id);
        void qc.invalidateQueries({ queryKey: keys.board(from) });
      }
      void qc.invalidateQueries({ queryKey: ['board-assets', boardId] });
      void qc.invalidateQueries({ queryKey: keys.board(boardId) });
      break;
    }
    case 'asset.removed':
    case 'asset.deleted': {
      const boardId = (p.board_id as string | undefined) ?? (env.room.startsWith('board:') ? roomId(env.room) : undefined);
      const id = p.asset_id as string | undefined;
      if (!boardId || !id) break;
      removeAssetFromBoard(qc, boardId, id);
      void qc.invalidateQueries({ queryKey: keys.board(boardId) });
      if (env.type === 'asset.deleted') {
        qc.setQueryData<Asset>(keys.asset(id), (old) => (old ? { ...old, deleted_at: old.deleted_at ?? env.ts } : old));
      }
      break;
    }
    case 'board.created':
    case 'board.restored':
    case 'board.updated':
    case 'board.moved':
    case 'board.deleted': {
      const board = p.board as Board | undefined;
      const parent = (p.parent_board_id as string | null | undefined) ?? board?.parent_board_id ?? null;
      if (board) {
        qc.setQueryData<Board>(keys.board(board.id), (old) => (old ? { ...old, ...board, effective_mask: old.effective_mask, breadcrumbs: old.breadcrumbs } : old));
        void qc.invalidateQueries({ queryKey: keys.libraryBoards(board.library_id) });
      } else void qc.invalidateQueries({ queryKey: ['library-boards'] });
      if (parent) {
        void qc.invalidateQueries({ queryKey: keys.boardChildren(parent) });
        void qc.invalidateQueries({ queryKey: keys.board(parent) });
      } else void qc.invalidateQueries({ queryKey: ['library-boards'] });
      if (env.type === 'board.moved') void qc.invalidateQueries({ queryKey: ['board-children'] });
      if (env.type === 'board.deleted') {
        const id = p.board_id as string | undefined;
        const subtree = Array.isArray(p.deleted_subtree_ids) ? (p.deleted_subtree_ids as string[]) : [];
        for (const b of new Set([...(id ? [id] : []), ...subtree])) {
          qc.removeQueries({ queryKey: keys.board(b) });
          qc.removeQueries({ queryKey: keys.boardChildren(b) });
          qc.removeQueries({ queryKey: keys.boardAssetsAll(b) });
        }
      }
      if (env.type === 'board.restored') void qc.invalidateQueries({ queryKey: ['board-children'] });
      void qc.invalidateQueries({ queryKey: ['board-tree'] });
      break;
    }
    case 'cover.changed': {
      const boardId = p.board_id as string | undefined;
      if (!boardId) break;
      qc.setQueryData<Board>(keys.board(boardId), (old) => (old ? { ...old, cover: (p.cover as Board['cover']) ?? old.cover, cover_mode: (p.cover_mode as Board['cover_mode']) ?? old.cover_mode } : old));
      void qc.invalidateQueries({ queryKey: ['board-children'] });
      void qc.invalidateQueries({ queryKey: ['library-boards'] });
      break;
    }
    // --- workspace room (H3): tags, fields, members and the workspace itself are cached for 5 min ------------------
    case 'tag.changed': {
      if (wsId) void qc.invalidateQueries({ queryKey: keys.tags(wsId) });
      const change = p.change as string | undefined;
      const tag = p.tag as { id: string; name: string } | undefined;
      if (change === 'deleted' && tag) {
        // names on cards and the open asset go stale: drop the name locally, refetch the rest lazily
        qc.setQueriesData<Infinite>({ queryKey: ['board-assets'] }, (old) => stripTagName(old, tag.name));
        qc.setQueriesData<Infinite>({ queryKey: ['share-board-assets'] }, (old) => stripTagName(old, tag.name));
      }
      if (change === 'deleted' || change === 'updated') {
        void qc.invalidateQueries({ queryKey: ['asset'] });
        void qc.invalidateQueries({ queryKey: ['board-assets'] });
        void qc.invalidateQueries({ queryKey: ['search'] });
      }
      break;
    }
    case 'custom_field.changed': {
      if (wsId) void qc.invalidateQueries({ queryKey: keys.fields(wsId) });
      const change = p.change as string | undefined;
      if (change === 'deleted' || change === 'updated') {
        void qc.invalidateQueries({ queryKey: ['asset'] });
        void qc.invalidateQueries({ queryKey: ['board-assets'] });
      }
      break;
    }
    case 'member.updated':
    case 'member.removed': {
      if (wsId) {
        void qc.invalidateQueries({ queryKey: keys.members(wsId) });
        void qc.invalidateQueries({ queryKey: keys.people(wsId) });
        void qc.invalidateQueries({ queryKey: keys.roles(wsId) });
      }
      // the principal mask comes from /auth/me (usePermission); roles/ownership changes also move effective masks
      void qc.invalidateQueries({ queryKey: keys.me() });
      if (p.permissions_changed !== false) {
        void qc.invalidateQueries({ queryKey: ['board'] });
        void qc.invalidateQueries({ queryKey: ['board-tree'] });
        void qc.invalidateQueries({ queryKey: ['library-boards'] });
      }
      break;
    }
    case 'workspace.updated': {
      const w = p.workspace as { id: string } | undefined;
      if (w) qc.setQueriesData<{ id: string }>({ queryKey: ['workspace'] }, (old) => (old && old.id === w.id ? { ...old, ...w } : old));
      void qc.invalidateQueries({ queryKey: ['workspace'] });
      void qc.invalidateQueries({ queryKey: keys.me() });
      if (wsId) void qc.invalidateQueries({ queryKey: keys.libraries(wsId) });
      break;
    }
    case 'presence.update': {
      hooks.onPresence?.((p.room as string) ?? env.room, (p.viewers as unknown[]) ?? []);
      break;
    }
    case 'download.ready': {
      hooks.onDownloadReady?.(p as { job_id: string; url: string; expires_at: string; asset_count: number });
      break;
    }
    default:
      // Release 1 events: each implementer registers an applier in its own section at the end of this file.
      for (const apply of RELEASE1_APPLIERS) if (apply(qc, env, p, wsId, hooks)) return env.type;
      for (const apply of RELEASE2_APPLIERS) if (apply(qc, env, p, wsId, hooks)) return env.type; // release2
      for (const apply of RELEASE3_APPLIERS) if (apply(qc, env, p, wsId, hooks)) return env.type; // release3
      return null;
  }
  return env.type;
}

/** Release 1 (docs/specs/release1-contract.md) appliers; return true when the event was handled. Append-only. */
type Release1Applier = (qc: QueryClient, env: WsEventEnvelope, p: P, wsId: string | undefined, hooks: ApplyHooks) => boolean;
const RELEASE1_APPLIERS: Release1Applier[] = [];
/** Release 2 appliers (`// --- release2/X ---` sections at the end of this file push here). Append-only. */
const RELEASE2_APPLIERS: Release1Applier[] = [];
/** Release 3 appliers (`// --- release3/X ---` sections at the end of this file push here). Append-only. */
const RELEASE3_APPLIERS: Release1Applier[] = [];

function stripTagName(old: Infinite | undefined, name: string): Infinite | undefined {
  if (!old) return old;
  let touched = false;
  const pages = old.pages.map((pg) => {
    let hit = false;
    const items = pg.items.map((c) => {
      const names = (c as AssetCard).tag_names;
      if (!names?.includes(name)) return c;
      hit = true;
      return { ...c, tag_names: names.filter((n) => n !== name) };
    });
    if (!hit) return pg;
    touched = true;
    return { ...pg, items };
  });
  return touched ? { ...old, pages } : old;
}

/** `replay_truncated` → refetch everything the room feeds (§4.6). */
export function invalidateRoom(qc: QueryClient, room: string): void {
  const id = roomId(room);
  if (room.startsWith('board:')) {
    void qc.invalidateQueries({ queryKey: ['board-assets', id] });
    void qc.invalidateQueries({ queryKey: keys.board(id) });
    void qc.invalidateQueries({ queryKey: keys.boardChildren(id) });
  } else if (room.startsWith('asset:')) {
    void qc.invalidateQueries({ queryKey: keys.asset(id) });
    void qc.invalidateQueries({ queryKey: keys.comments(id) });
    void qc.invalidateQueries({ queryKey: keys.versions(id) });
  } else {
    void qc.invalidateQueries({ queryKey: ['library-boards'] });
    void qc.invalidateQueries({ queryKey: ['board-children'] });
  }
}

// --- release1/C ---
/** F5 `asset.merged`: the survivor's card changed (tags, fields, comment count) and the merged ids are gone everywhere. */
function applyAssetMerged(qc: QueryClient, p: P, wsId: string | undefined): void {
  const assetId = p.asset_id as string;
  const merged = (p.merged_asset_ids as string[] | undefined) ?? [];
  const card = p.asset as AssetCard | undefined;
  if (card) patchCardInPages(qc, card);
  for (const id of merged) {
    qc.removeQueries({ queryKey: keys.asset(id) });
    for (const query of qc.getQueryCache().findAll({ queryKey: ['board-assets'] })) {
      const boardId = query.queryKey[1] as string;
      removeAssetFromBoard(qc, boardId, id);
    }
  }
  void qc.invalidateQueries({ queryKey: keys.asset(assetId) });
  void qc.invalidateQueries({ queryKey: ['duplicates', ...(wsId ? [wsId] : [])] });
  void qc.invalidateQueries({ queryKey: ['search'] });
}

/** G2 `tag.merged`: refresh the catalogue, re-point merged ids inside active filters and refetch tag-shaped data. */
function applyTagMerged(qc: QueryClient, p: P, wsId: string | undefined): void {
  const tagId = p.tag_id as string;
  const merged = (p.merged_tag_ids as string[] | undefined) ?? [];
  if (wsId) {
    void qc.invalidateQueries({ queryKey: keys.tags(wsId) });
    void qc.invalidateQueries({ queryKey: keys.savedSearches(wsId) });
    void qc.invalidateQueries({ queryKey: keys.aiTagReviewAll(wsId) });
  } else {
    void qc.invalidateQueries({ queryKey: ['tags'] });
  }
  for (const hook of TAG_FILTER_REWRITERS) hook(merged, tagId);
  void qc.invalidateQueries({ queryKey: ['search'] });
  void qc.invalidateQueries({ queryKey: ['board-assets'] });
  void qc.invalidateQueries({ queryKey: ['asset'] });
}

/** Filter stores (search page, board filter spine) register here to strip merged tag ids from their live state. */
export type TagFilterRewriter = (mergedTagIds: string[], intoTagId: string) => void;
const TAG_FILTER_REWRITERS: TagFilterRewriter[] = [];
export function onTagMerged(fn: TagFilterRewriter): () => void {
  TAG_FILTER_REWRITERS.push(fn);
  return () => {
    const i = TAG_FILTER_REWRITERS.indexOf(fn);
    if (i >= 0) TAG_FILTER_REWRITERS.splice(i, 1);
  };
}
/** Pure helper for those stores: replace merged ids with the survivor, de-duplicated, order kept. */
export function rewriteTagIds(ids: readonly string[], mergedTagIds: readonly string[], intoTagId: string): string[] {
  const out: string[] = [];
  for (const id of ids) {
    const next = mergedTagIds.includes(id) ? intoTagId : id;
    if (!out.includes(next)) out.push(next);
  }
  return out;
}

RELEASE1_APPLIERS.push((qc, env, p, wsId) => {
  switch (env.type) {
    case 'asset.merged':
      applyAssetMerged(qc, p, wsId);
      return true;
    case 'tag.merged':
      applyTagMerged(qc, p, wsId);
      return true;
    default:
      return false;
  }
});

// --- release1/A ---
import { useSessionStore } from '@/stores/session';
import type { Schemas as SchemasA } from '@/lib/api/types';
type BoardViewA = SchemasA['BoardView'];

/**
 * F2 `view.created|updated|deleted` on the board room. The gateway fans out to every connection in the room, so a
 * private view's event reaches other members too: anything private that the current member does not own is dropped
 * here (the API never lists it for them either). Payloads without `view` (deletes, lean relays) just invalidate.
 */
function applyViewEvent(qc: QueryClient, env: WsEventEnvelope, p: P): void {
  const boardId = (p.board_id as string | undefined) ?? (env.room.startsWith('board:') ? roomId(env.room) : undefined);
  if (!boardId) return;
  const viewId = p.view_id as string | undefined;
  const view = p.view as BoardViewA | undefined;
  const me = useSessionStore.getState().membership?.member_id ?? null;
  const key = keys.boardViews(boardId);
  if (env.type === 'view.deleted' || (view && view.visibility === 'private' && view.owner_member_id !== me)) {
    if (viewId) qc.setQueryData<BoardViewA[]>(key, (old) => old?.filter((v) => v.id !== viewId));
  } else if (view) {
    qc.setQueryData<BoardViewA[]>(key, (old) => {
      if (!old) return old;
      const rest = old.filter((v) => v.id !== view.id).map((v) => (view.is_default && v.is_default ? { ...v, is_default: false } : v));
      return [...rest, view].sort((a, b) => Number(b.visibility === 'board') - Number(a.visibility === 'board') || a.sort_order - b.sort_order || a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) || a.id.localeCompare(b.id));
    });
  }
  void qc.invalidateQueries({ queryKey: key });
}

RELEASE1_APPLIERS.push((qc, env, p) => {
  switch (env.type) {
    case 'view.created':
    case 'view.updated':
    case 'view.deleted':
      applyViewEvent(qc, env, p);
      return true;
    default:
      return false;
  }
});

// --- release1/B ---
import type { MemberPin } from '@/lib/api/types';
import { useTidyPrefs } from '@/stores/gridPrefs';

/** S7a `asset.archived` / `asset.unarchived` (`WsAssetUpdatedPayload`): patch the card; archived cards leave the grids unless "Show archived" is on. */
function applyAssetArchived(qc: QueryClient, p: P, env: WsEventEnvelope, archived: boolean): void {
  const card = p.asset as AssetCard | undefined;
  const id = (p.asset_id as string | undefined) ?? card?.id;
  if (!id) return;
  const archivedAt = card?.archived_at ?? (archived ? env.ts : null);
  if (card) patchCardInPages(qc, card);
  else patchCardInPages(qc, { id, archived_at: archivedAt } as Partial<AssetCard> & { id: string });
  qc.setQueryData<Asset>(keys.asset(id), (old) => (old ? { ...old, ...(card ? { ...card, media: old.media, tags: old.tags, custom_field_values: old.custom_field_values, boards: old.boards, active_version: old.active_version } : {}), archived_at: archivedAt } : old));
  if (archived && !useTidyPrefs.getState().showArchived) {
    for (const query of qc.getQueryCache().findAll({ queryKey: ['board-assets'] })) removeAssetFromBoard(qc, query.queryKey[1] as string, id);
  } else if (!archived) {
    // the card may belong on pages that dropped it; refetch the boards it lives on
    for (const b of card?.board_ids ?? []) void qc.invalidateQueries({ queryKey: ['board-assets', b] });
    if (!card) void qc.invalidateQueries({ queryKey: ['board-assets'] });
  }
  void qc.invalidateQueries({ queryKey: ['search'] });
  void qc.invalidateQueries({ queryKey: ['pins'] });
}

/** S7a `board.archived` / `board.unarchived` (`WsBoardArchivedPayload`): flag the subtree and refresh the tree listings. */
function applyBoardArchived(qc: QueryClient, p: P, env: WsEventEnvelope, archived: boolean): void {
  const board = p.board as Board | undefined;
  const id = (p.board_id as string | undefined) ?? board?.id;
  if (!id) return;
  const parent = (p.parent_board_id as string | null | undefined) ?? board?.parent_board_id ?? null;
  const subtree = Array.isArray(p.archived_subtree_ids) ? (p.archived_subtree_ids as string[]) : [];
  const archivedAt = board?.archived_at ?? (archived ? env.ts : null);
  for (const b of new Set([id, ...subtree])) {
    qc.setQueryData<Board>(keys.board(b), (old) => (old ? { ...old, ...(b === id && board ? { ...board, effective_mask: old.effective_mask, breadcrumbs: old.breadcrumbs } : {}), archived_at: archivedAt } : old));
    void qc.invalidateQueries({ queryKey: keys.boardChildren(b) });
    void qc.invalidateQueries({ queryKey: ['board-assets', b] });
  }
  if (parent) void qc.invalidateQueries({ queryKey: keys.boardChildren(parent) });
  else void qc.invalidateQueries({ queryKey: ['library-boards'] });
  if (board) void qc.invalidateQueries({ queryKey: keys.libraryBoards(board.library_id) });
  void qc.invalidateQueries({ queryKey: ['board-tree'] });
  void qc.invalidateQueries({ queryKey: ['pins'] });
}

RELEASE1_APPLIERS.push((qc, env, p, wsId) => {
  switch (env.type) {
    case 'asset.archived':
      applyAssetArchived(qc, p, env, true);
      return true;
    case 'asset.unarchived':
      applyAssetArchived(qc, p, env, false);
      return true;
    case 'board.archived':
      applyBoardArchived(qc, p, env, true);
      return true;
    case 'board.unarchived':
      applyBoardArchived(qc, p, env, false);
      return true;
    case 'pin.changed': {
      // S1: delivered to the pinning member only; the payload is the full list
      const ws = (p.workspace_id as string | undefined) ?? wsId;
      if (ws && Array.isArray(p.items)) qc.setQueryData<MemberPin[]>(keys.pins(ws), p.items as MemberPin[]);
      else if (ws) void qc.invalidateQueries({ queryKey: keys.pins(ws) });
      return true;
    }
    case 'trash.purged': {
      // S7a: purged rows are gone for good; drop their queries and refetch the trash
      const ws = (p.workspace_id as string | undefined) ?? wsId;
      for (const ref of Array.isArray(p.items) ? (p.items as Array<{ kind: string; id: string }>) : []) {
        if (ref.kind === 'asset') qc.removeQueries({ queryKey: keys.asset(ref.id) });
        else {
          qc.removeQueries({ queryKey: keys.board(ref.id) });
          qc.removeQueries({ queryKey: keys.boardChildren(ref.id) });
          qc.removeQueries({ queryKey: keys.boardAssetsAll(ref.id) });
        }
      }
      if (ws) void qc.invalidateQueries({ queryKey: keys.trashAll(ws) });
      else void qc.invalidateQueries({ queryKey: ['trash'] });
      return true;
    }
    default:
      return false;
  }
});

// --- release2/A --- smart boards (S3) and system boards (S4)
/** Side-effect-only hooks that see every envelope before the switch (which claims `board.*` / `asset.*` first). */
export type Release2Tap = (qc: QueryClient, env: WsEventEnvelope, p: P, wsId: string | undefined) => void;
/** Extension point: lanes B / C may push their own taps here instead of adding hooks to `applyEvent`. */
export const RELEASE2_TAPS: Release2Tap[] = [];

/** System board counts are exact aggregates: one refetch per burst of asset events, not one per event. */
export const SYSTEM_BOARDS_REFRESH_MS = 2000;
const systemBoardTimers = new Map<string, ReturnType<typeof setTimeout>>();
function refreshSystemBoardsSoon(qc: QueryClient, wsId: string | undefined): void {
  const k = wsId ?? '*';
  if (systemBoardTimers.has(k)) return;
  systemBoardTimers.set(
    k,
    setTimeout(() => {
      systemBoardTimers.delete(k);
      void qc.invalidateQueries({ queryKey: wsId ? keys.systemBoards(wsId) : ['system-boards'] });
    }, SYSTEM_BOARDS_REFRESH_MS),
  );
}
const SYSTEM_BOARD_EVENTS = new Set(['asset.updated', 'asset.created', 'asset.archived', 'asset.unarchived', 'asset.deleted', 'asset.restored', 'asset.removed', 'asset.moved']);

/**
 * An open smart board hears its watched boards' asset events (SmartBoardPage joins their rooms) but cannot tell from
 * an event whether the asset now matches the definition: refetch its listing once per burst, never once per event.
 */
export const SMART_BOARD_REFRESH_MS = 2000;
const smartBoardTimers = new Map<string, ReturnType<typeof setTimeout>>();
function refreshOpenSmartBoardsSoon(qc: QueryClient): void {
  for (const q of qc.getQueryCache().findAll({ queryKey: ['board-assets'], type: 'active' })) {
    const id = q.queryKey[1] as string;
    if (smartBoardTimers.has(id) || qc.getQueryData<Board>(keys.board(id))?.kind !== 'smart') continue;
    smartBoardTimers.set(
      id,
      setTimeout(() => {
        smartBoardTimers.delete(id);
        void qc.invalidateQueries({ queryKey: keys.boardAssetsAll(id) });
        void qc.invalidateQueries({ queryKey: ['board-facets', id] });
      }, SMART_BOARD_REFRESH_MS),
    );
  }
}

RELEASE2_TAPS.push((qc, env, p, wsId) => {
  if (env.type === 'board.updated') {
    // a new definition (or sort) of a smart board changes its whole listing
    const board = p.board as Board | undefined;
    const id = board?.id ?? (p.board_id as string | undefined);
    if (id && (board?.kind ?? qc.getQueryData<Board>(keys.board(id))?.kind) === 'smart') {
      void qc.invalidateQueries({ queryKey: keys.boardAssetsAll(id) });
      void qc.invalidateQueries({ queryKey: ['board-facets', id] });
    }
  }
  if (SYSTEM_BOARD_EVENTS.has(env.type)) {
    refreshSystemBoardsSoon(qc, wsId);
    refreshOpenSmartBoardsSoon(qc);
  }
});

// --- release2/B --- field policies (G3), tag groups and curated tag creation (G1)
type TagGroupRow = { id: string; sort_order: number; name: string };

RELEASE2_APPLIERS.push((qc, env, p, wsId) => {
  const ws = (p.workspace_id as string | undefined) ?? wsId;
  switch (env.type) {
    case 'field_policy.changed': {
      // descendants inherit the board's rows: every cached policy set may move; flipped cards arrive as asset.updated
      void qc.invalidateQueries({ queryKey: ['field-policies'] });
      if (typeof p.board_id === 'string') void qc.invalidateQueries({ queryKey: keys.boardAssetsAll(p.board_id) });
      void qc.invalidateQueries({ queryKey: ['asset'] });
      return true;
    }
    case 'tag_group.changed': {
      if (!ws) return true;
      const group = p.tag_group as TagGroupRow | undefined;
      const gone = p.change === 'deleted';
      qc.setQueryData<TagGroupRow[]>(keys.tagGroups(ws), (old) => {
        if (!old) return old;
        const rest = old.filter((g) => g.id !== p.tag_group_id);
        return gone || !group ? rest : [...rest, group].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
      });
      // a deleted group ungroups its tags (FK SET NULL); counts move on assignment
      if (gone) void qc.invalidateQueries({ queryKey: keys.tags(ws) });
      return true;
    }
    case 'workspace_policy.changed': {
      // shared with lane C (rights_on_expiry): only the tag_creation key is B's
      if (!('tag_creation' in p) || !ws) return false;
      qc.setQueryData(keys.tagPolicy(ws), { tag_creation: p.tag_creation });
      return true;
    }
    default:
      return false;
  }
});

// --- release2/C --- usage rights expiry (C4) and board retention (S7b)
/**
 * `rights.expired` / `retention.applied` (workspace room, SQL-emitted per batch): patch the listed cards — expired
 * rights, and archived ones leave the listings as for `asset.archived` — or refetch every asset list when `truncated`.
 */
function applyBatchArchived(qc: QueryClient, ids: readonly string[], ts: string): void {
  const hide = !useTidyPrefs.getState().showArchived;
  const boards = hide ? qc.getQueryCache().findAll({ queryKey: ['board-assets'] }).map((q) => q.queryKey[1] as string) : [];
  for (const id of ids) {
    patchCardInPages(qc, { id, archived_at: ts });
    qc.setQueryData<Asset>(keys.asset(id), (old) => (old ? { ...old, archived_at: old.archived_at ?? ts } : old));
    for (const b of boards) removeAssetFromBoard(qc, b, id);
  }
  void qc.invalidateQueries({ queryKey: ['search'] });
  void qc.invalidateQueries({ queryKey: ['pins'] });
}
function refetchAssetLists(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: ['board-assets'] });
  void qc.invalidateQueries({ queryKey: ['search'] });
  void qc.invalidateQueries({ queryKey: ['asset'] });
}
RELEASE2_APPLIERS.push((qc, env, p) => {
  if (env.type !== 'rights.expired' && env.type !== 'retention.applied') return false;
  const ids = Array.isArray(p.asset_ids) ? (p.asset_ids as string[]) : [];
  if (p.truncated) refetchAssetLists(qc);
  else if (env.type === 'rights.expired') {
    for (const id of ids) {
      patchCardInPages(qc, { id, rights_status: 'expired' });
      qc.setQueryData<Asset>(keys.asset(id), (old) => (old ? { ...old, rights_status: 'expired', rights: old.rights ? { ...old.rights, status: 'expired' } : old.rights } : old));
    }
    if (p.action === 'archive') applyBatchArchived(qc, ids, env.ts);
  } else applyBatchArchived(qc, ids, env.ts);
  return true;
});
// `workspace_policy.changed` without `tag_creation` reaches here (B's applier passes it on): the rights key is C's
RELEASE2_APPLIERS.push((qc, env, p, wsId) => {
  if (env.type !== 'workspace_policy.changed') return false;
  if (wsId && typeof p.rights_on_expiry === 'string') qc.setQueryData(keys.rightsPolicy(wsId), { on_expiry: p.rights_on_expiry });
  else if (wsId) void qc.invalidateQueries({ queryKey: keys.rightsPolicy(wsId) });
  return true;
});

// --- release3/A --- notifications (REV-1): member-scoped events (the gateway delivers them to the recipient only)
import { markReadInCache, type Notification as NotificationR3A, type NotificationPage as NotificationPageR3A } from '@/lib/api/queries';

/** `notification.created`: prepend the row to every cached inbox page set it matches and bump `unread_count`. */
function applyNotificationCreated(qc: QueryClient, n: NotificationR3A): void {
  const wsId = n.workspace_id;
  let touched = false;
  qc.setQueriesData<{ pages: NotificationPageR3A[]; pageParams: unknown[] }>({ queryKey: keys.notificationsAll(wsId) }, (old) => {
    if (!old || !old.pages.length) return old;
    touched = true;
    const pages = old.pages.map((pg, i) => {
      const unread_count = pg.unread_count + (n.read_at ? 0 : 1);
      if (i !== 0 || pg.items.some((it) => it.id === n.id)) return { ...pg, unread_count };
      return { ...pg, unread_count, items: [n, ...pg.items] };
    });
    return { ...old, pages };
  });
  if (!touched) void qc.invalidateQueries({ queryKey: keys.notificationsAll(wsId) });
  if (n.board_id) void qc.invalidateQueries({ queryKey: keys.boardActivityAll(n.board_id) });
}

RELEASE3_APPLIERS.push((qc, env, p) => {
  if (env.type === 'notification.created') {
    if (typeof p.id === 'string' && typeof p.workspace_id === 'string') applyNotificationCreated(qc, p as unknown as NotificationR3A);
    return true;
  }
  if (env.type === 'notification.read') {
    const wsId = p.workspace_id as string | undefined;
    if (!wsId) return true;
    const ids = Array.isArray(p.ids) ? (p.ids as string[]) : [];
    // another tab / device marked rows read: mirror it (all → everything)
    if (p.all === true || !ids.length) markReadInCache(qc, wsId, null);
    else markReadInCache(qc, wsId, ids);
    return true;
  }
  return false;
});

// --- release3/C --- export presets (EXP-1)
/** `preset.changed` (workspace room): clients refetch `listDownloadPresets`; the payload carries only the id + change. */
RELEASE3_APPLIERS.push((qc, env, p, wsId) => {
  if (env.type !== 'preset.changed') return false;
  const ws = typeof p.workspace_id === 'string' ? p.workspace_id : wsId;
  if (ws) void qc.invalidateQueries({ queryKey: keys.downloadPresets(ws) });
  else void qc.invalidateQueries({ queryKey: ['download-presets'] });
  return true;
});
